import { ITEM_TYPES, SUPPORTED_CURRENCIES } from './constants';

export const formatCurrency = (value, locale = 'cs-CZ', currencyCode = 'CZK') => {
  if (typeof value !== 'number' || isNaN(value)) {
    value = 0;
  }
  
  const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode) || { symbol: currencyCode, code: currencyCode };

  // 1. Získáme správně naformátované číslo pro danou lokalitu (s čárkami/tečkami/mezerami)
  const numberPart = new Intl.NumberFormat(locale, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(value);

  // 2. Ručně umístíme správný symbol měny na správné místo
  if (locale.startsWith('en')) {
    // Pro anglické lokalizace dáváme symbol na začátek
    // Písmenný symbol (CHF) potřebuje mezeru, jinak vznikne "CHF1,000"
    const separator = /[A-Za-z]$/.test(currencyInfo.symbol) ? ' ' : '';
    return `${currencyInfo.symbol}${separator}${numberPart}`;
  } else {
    // Pro ostatní (v našem případě cs-CZ) dáváme symbol na konec
    return `${numberPart} ${currencyInfo.symbol}`;
  }
};

export const formatLargeCurrency = (value, locale = 'cs-CZ', currencyCode = 'CZK') => {
    if (typeof value !== 'number' || isNaN(value)) {
      value = 0;
    }

    const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode) || { symbol: currencyCode };
    const format = (num) => new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(num);

    // Manuální zkrácení pro velké částky — "mil./mld." is Czech; English reads "$12.6M"
    if (locale.startsWith('en')) {
        const sep = /[A-Za-z]$/.test(currencyInfo.symbol) ? ' ' : '';
        if (Math.abs(value) >= 1_000_000_000) return `${currencyInfo.symbol}${sep}${format(value / 1_000_000_000)}B`;
        if (Math.abs(value) >= 1_000_000) return `${currencyInfo.symbol}${sep}${format(value / 1_000_000)}M`;
    }
    if (Math.abs(value) >= 1_000_000_000) return `${format(value / 1_000_000_000)} mld. ${currencyInfo.symbol}`;
    if (Math.abs(value) >= 1_000_000) return `${format(value / 1_000_000)} mil. ${currencyInfo.symbol}`;
    
    // Pro menší částky použijeme naši novou robustní funkci
    return formatCurrency(value, locale, currencyCode);
};

export const formatPercentage = (value, locale = 'cs-CZ', decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) {
     return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(0);
  }
  return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
};


export const processItemFormData = (item) => {
    const processedItem = { ...item };
    const fieldsToProcess = { 
        'currentValue':'number', 'annualAppreciationRate':'percentage', 'mortgageInitialAmount':'number', 
        'monthlyRentalIncome':'number', 'monthlyCosts':'number', 'monthlyContribution':'number', 
        'annualGrowthRate':'percentage', 'annualIncomeYield':'percentage', 'monthlyExpense':'number', 
        'monthlyIncome':'number', 'loanAmount':'number', 'monthlyPayment':'number', 
        'interestRate':'percentage', 'mortgageInterestRate':'percentage', 'rentalAndCostsGrowthRate': 'percentage',
        // Nová pole pro simulaci
        'sim_annualAppreciationRate_min': 'percentage', 'sim_annualAppreciationRate_avg': 'percentage', 'sim_annualAppreciationRate_max': 'percentage',
        'sim_rentalAndCostsGrowthRate_min': 'percentage', 'sim_rentalAndCostsGrowthRate_avg': 'percentage', 'sim_rentalAndCostsGrowthRate_max': 'percentage',
        'sim_annualGrowthRate_min': 'percentage', 'sim_annualGrowthRate_avg': 'percentage', 'sim_annualGrowthRate_max': 'percentage'
    };
    
    for (const field in fieldsToProcess) {
        if (Object.hasOwnProperty.call(processedItem, field) && processedItem[field] != null) {
            const valueStr = String(processedItem[field]).replace(',', '.').trim();
            let numericValue = parseFloat(valueStr);
            if (valueStr === '' || isNaN(numericValue)) {
                numericValue = (fieldsToProcess[field] === 'percentage' && valueStr === '') ? null : 0;
            }
            if (fieldsToProcess[field] === 'percentage' && numericValue !== null) {
                 numericValue /= 100;
            }
            processedItem[field] = numericValue;
        }
    }
    if (Object.prototype.hasOwnProperty.call(processedItem, 'note')) {
        const noteValue = typeof processedItem.note === 'string' ? processedItem.note.trim() : processedItem.note;
        processedItem.note = noteValue ? noteValue : null;
    }
    if (processedItem.type === ITEM_TYPES.LOAN) { processedItem.loanTermTotalMonths = parseInt(processedItem.loanTermUnit === 'years' ? parseFloat(processedItem.loanTermDisplay || 0) * 12 : parseFloat(processedItem.loanTermDisplay || 0), 10) || 0; }
    if (processedItem.type === ITEM_TYPES.REAL_ESTATE && processedItem.hasMortgage) { processedItem.mortgageTermTotalMonths = parseInt(processedItem.mortgageTermUnit === 'years' ? parseFloat(processedItem.mortgageTermDisplay || 0) * 12 : parseFloat(processedItem.mortgageTermDisplay || 0), 10) || 0; }
    
    return processedItem;
};


export const getNeonColorValues = (type) => {
    switch(type) {
        case ITEM_TYPES.REAL_ESTATE: return '59,130,246';
        case ITEM_TYPES.OTHER_INVESTMENT: return '34,197,94';
        case ITEM_TYPES.INCOME: return '16,185,129';
        case ITEM_TYPES.EXPENSE: return '239,68,68';
        case ITEM_TYPES.LOAN: return '245,158,11';
        case 'cyan': return '6,182,212';
        case 'green': return '34,197,94';
        case 'blue': return '59,130,246';
        case 'purple': return '168,85,247';
        case 'teal': return '20,184,166';
        case 'indigo': return '99,102,241';
        case 'violet': return '139,92,246';
        default: return '107,114,128';
    }
};

export const getBorderColorClass = (type, theme) => {
    if (theme === 'dark') {
        switch(type) {
            case ITEM_TYPES.REAL_ESTATE: return 'border-sky-500';
            case ITEM_TYPES.OTHER_INVESTMENT: return 'border-green-500';
            case ITEM_TYPES.INCOME: return 'border-emerald-500';
            case ITEM_TYPES.EXPENSE: return 'border-red-500';
            case ITEM_TYPES.LOAN: return 'border-amber-500';
            case 'cyan': return 'border-cyan-500';
            case 'green': return 'border-green-500';
            case 'blue': return 'border-blue-500';
            case 'purple': return 'border-purple-500';
            case 'teal': return 'border-teal-500';
            case 'indigo': return 'border-indigo-500';
            case 'violet': return 'border-violet-500';
            default: return 'border-gray-700';
        }
    }
    return 'border-gray-300';
};

export const getItemIconColor = (type, isActive = true, isDarkMode = false) => {
    if (!isActive) return isDarkMode ? 'text-gray-500' : 'text-gray-400';
    if (isDarkMode) {
        switch(type) {
            case ITEM_TYPES.REAL_ESTATE: return 'text-sky-400';
            case ITEM_TYPES.OTHER_INVESTMENT: return 'text-lime-400';
            case ITEM_TYPES.INCOME: return 'text-emerald-400';
            case ITEM_TYPES.EXPENSE: return 'text-red-400';
            case ITEM_TYPES.LOAN: return 'text-amber-400';
            default: return 'text-gray-300';
        }
    } else {
        switch(type) {
            case ITEM_TYPES.REAL_ESTATE: return 'text-blue-500';
            case ITEM_TYPES.OTHER_INVESTMENT: return 'text-green-500';
            case ITEM_TYPES.INCOME: return 'text-emerald-500';
            case ITEM_TYPES.EXPENSE: return 'text-red-500';
            case ITEM_TYPES.LOAN: return 'text-yellow-500';
            default: return 'text-gray-500';
        }
    }
};
// A failed write, said in words a person can act on. Firebase's own messages
// ("Missing or insufficient permissions.") are for developers.
export const errorMessage = (t, error) => {
    const code = String(error?.code || '').replace(/^firestore\//, '');
    if (code === 'permission-denied' || code === 'unauthenticated') return t('errors.permission');
    if (code === 'unavailable' || code === 'deadline-exceeded' || /network|offline/i.test(error?.message || '')) return t('errors.offline');
    if (code === 'resource-exhausted') return t('errors.busy');
    return t('errors.generic');
};
